import ConfigurationForm from '@/components/data/configuration/ConfigurationForm';
import SourceDataForm from '@/components/data/sourceData/SourceDataForm';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationById from '@/hooks/data/useConfigurationById';
import useMetadata from '@/hooks/data/useMetadata';
import usePatient from '@/hooks/data/usePatient';
import usePatients from '@/hooks/data/usePatients';
import useSourceDataPreviewByTrialCodeAndPatientStudyNumber from '@/hooks/data/useSourceDataPreviewByTrialCodeAndPatientStudyNumber';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { BasicAlert, ChosenThemeActionContext, ChosenThemeContext } from '@ngt/forms';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ISourceData, ISourceDataConfiguration, ISourceDataFinding, ISourceDataVerificationPatient, SourceDataFindingCollectionResponse, SourceDataSingleResponse } from '../api/dtos';
import { IPatient, SourceDataVerificationPermission } from '@/api/dtos';
import { AppBar, Autocomplete, Box, Button, IconButton, LinearProgress, styled, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useTrialConfigurationByTrialCode from '@/hooks/data/useTrialConfigurationByTrialCode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun } from '@fortawesome/pro-duotone-svg-icons';
import useInstitution from '@/hooks/data/useInstitution';
import useSourceDataFindingsByPatientStudyNumber from '@/hooks/data/useSourceDataFindingsByPatientStudyNumber';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import usePermissions from '@/hooks/utility/usePermissions';


const Page = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const Buffer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...(theme.mixins.toolbar as any),
    '@media (min-width: 600px)': {
        minHeight: '64px'
    }
}));

const PatientData: FC = () => {
    const { trialCode } = useParams();
    const [search] = useSearchParams();
    const previewId = search.get('previewId');

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme])
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });

    const { data: patients, loading: patientsLoading, validating: patientsUpdating } = usePatients(trialCode!, { revalidateOnFocus: false });
    
    const { data: trialConfiguration, loading: trialConfigurationLoading, validating: trialConfigurationUpdating } = useTrialConfigurationByTrialCode(!!metadata ? trialCode : undefined, { revalidateOnFocus: false });

    const [ patient, setPatient ] = useState<ISourceDataVerificationPatient | undefined>(undefined);
    const [ selectedPatient, setSelectedPatient ] = useState<ISourceDataVerificationPatient | undefined>(undefined);

    const [ configuration, setConfiguration ] = useState<ISourceDataConfiguration | undefined>();
    
    const channelRef = useRef<BroadcastChannel>();

    const onUpdate = useCallback(() => {
        channelRef.current?.postMessage({ type: 'request-configuration' });
    }, [patient, setSelectedPatient, setConfiguration, channelRef]);
    
    const { data: sourceData, loading: sourceDataLoading, validating: sourceDataUpdating, mutate: mutateSourceData } = useSourceDataPreviewByTrialCodeAndPatientStudyNumber(trialCode, selectedPatient?.studyNumber, configuration, { revalidateOnFocus: false });
    const { data: institution, loading: institutionLoading, validating: institutionUpdating  } = useInstitution(trialCode, selectedPatient?.institutionCode, { revalidateOnFocus: false });


    useEffect(() => {
        const channel = new BroadcastChannel(previewId ?? 'NO ID');
    
        channel.onmessage = ((ev: MessageEvent<{ type: string, configuration?: ISourceDataConfiguration }>) => {
            if (ev.data.type === 'configuration-update') {
                setConfiguration(ev.data.configuration);
                setSelectedPatient(patient);
                setTimeout(() => mutateSourceData(new SourceDataSingleResponse({data: sourceData}), true), 0);
            }
        });

        channelRef.current = channel;

        return () => {
            channel.close();
            channelRef.current = undefined;
        }

    }, [previewId, setConfiguration, mutateSourceData, sourceData, setSelectedPatient, patient])

    const updateSourceData = useCallback(async (newData?: ISourceData) => {
        if (newData) {
            await mutateSourceData(new SourceDataSingleResponse({
                data: newData
            }), true);
        }
    }, [mutateSourceData, sourceData]);

    const loadingData = patientsLoading || (patient?.institutionCode && (institutionLoading || institutionUpdating)) || patientsUpdating || sourceDataLoading || /*sourceDataUpdating ||*/ trialConfigurationLoading || trialConfigurationUpdating;


    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;

    if (!hasEditConfiguration && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }

    if (!previewId) {
        return (
            <BasicAlert
                title="No Preview ID"
                description="The configuration preview must be opened via the preview button on the edit configuration page."
                severity="error"
                icon={faExclamationTriangle}
            />
        )
    }

    return (
        <>
            <PageTitle title={`Preview Configuration${TITLE_SEPARATOR}${trial?.name}`} />
            <AppBar>
                <Toolbar>
                    <Typography 
                        variant="h6" 
                        sx={{
                            color: 'common.white',
                            textDecoration: 'none'
                        }}
                    >
                        Preview
                    </Typography>
                    <Tooltip 
                        title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    >
                        <IconButton
                            aria-label="theme"
                            color="inherit"
                            onClick={changeTheme}
                            sx={{ml: 'auto', mr: 2}}
                        >
                            <FontAwesomeIcon icon={chosenTheme === 'dark' ? faMoon : faSun } />
                        </IconButton>
                    </Tooltip>
                    <Autocomplete
                        disablePortal
                        options={patients ?? []}
                        value={patient ?? null}
                        onChange={(e, value) => setPatient(value ?? undefined)}
                        getOptionLabel={(data) => data.studyNumber ?? 'Unknown'}
                        sx={{ width: 200, mr: 2 }}
                        renderInput={(params) => <TextField {...params} sx={{ '& > div': { backgroundColor: chosenTheme !== 'dark' ? 'common.white' : undefined}}} size="small" placeholder="Select a participant" />}
                    />
                    <LoadingButton
                        color="inherit"
                        onClick={onUpdate}
                        loading={loading}
                    >
                        Update
                    </LoadingButton>
                </Toolbar>
            </AppBar>
            <Buffer />
            {
                (loading || sourceDataUpdating) && (
                    <Box
                        sx={{

                            position: 'relative'
                        }}
                    >
                        <LinearProgress 
                            color="secondary"
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0
                            }}
                        />
                    </Box>
                )
            }
            {
                !loading && (
                    <Page>
                        {
                            (!selectedPatient || !configuration) && (
                                <BasicAlert
                                    title="No Patient Selected"
                                    description="Select a participant and click the update button."
                                    severity="info"
                                    icon={faInfoCircle}
                                />
                            )
                        }
                        {
                            selectedPatient && configuration && sourceData && (
                                <>
                                    <SourceDataForm 
                                        configuration={configuration}
                                        trialConfiguration={trialConfiguration}
                                        data={sourceData}
                                        metadata={metadata}
                                        trial={trial}
                                        patient={patient}
                                        institution={institution}
                                        updateSourceData={updateSourceData}
                                    />
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        onClick={() => window.close()}
                                    >
                                        Close
                                    </Button>
                                </>
                            )
                        }
                    </Page>
                )
            }
        </>
    );
}

export default PatientData;